/**
 * PrepareServerModal — panel ITE „Serwery baz": przygotowanie serwera klienta.
 *
 * Jednorazowo loguje się kontem administratora PostgreSQL klienta, zakłada konto aplikacji
 * oraz konta odczytu i procedur (nowe hasła, zapisywane zaszyfrowane). Dane administratora
 * NIE są nigdzie zapisywane — hasło czyścimy ze stanu po sukcesie i przy zamknięciu okna.
 * Błędy serwera → czytelne komunikaty (docs/ADR/0113b §6).
 */

import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { DbServerChecksList } from '@/components/db-servers/DbServerChecksList';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import {
  type DbServerCheckResult,
  type DbServerDto,
  dbServerErrorInfo,
  usePrepareDbServer,
} from '@/lib/db-servers/api';

export interface PrepareServerModalProps {
  open: boolean;
  server: DbServerDto | null;
  onClose: () => void;
}

const ERROR_KEYS: Record<string, string> = {
  SUPERUSER_CONNECT_FAILED: 'admin.dbServers.prepare.errors.SUPERUSER_CONNECT_FAILED',
  NOT_SUPERUSER: 'admin.dbServers.prepare.errors.NOT_SUPERUSER',
  EXTENSIONS_MISSING: 'admin.dbServers.prepare.errors.EXTENSIONS_MISSING',
  UNSUPPORTED_VERSION: 'admin.dbServers.prepare.errors.UNSUPPORTED_VERSION',
  VERIFY_FAILED: 'admin.dbServers.prepare.errors.VERIFY_FAILED',
  BUSY: 'admin.dbServers.prepare.errors.BUSY',
  DISABLED: 'admin.dbServers.prepare.errors.DISABLED',
  SERVER_NOT_FOUND: 'admin.dbServers.prepare.errors.SERVER_NOT_FOUND',
  FORBIDDEN: 'admin.dbServers.prepare.errors.FORBIDDEN',
  RATE_LIMITED: 'admin.dbServers.prepare.errors.RATE_LIMITED',
  PREPARE_FAILED: 'admin.dbServers.prepare.errors.PREPARE_FAILED',
};

export function PrepareServerModal({ open, server, onClose }: PrepareServerModalProps) {
  if (!open || !server) return null;
  return <PrepareServerModalBody server={server} onClose={onClose} />;
}

function PrepareServerModalBody({ server, onClose }: { server: DbServerDto; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const prepare = usePrepareDbServer();
  const formId = useId();

  const [user, setUser] = useState('postgres');
  const [password, setPassword] = useState('');
  const [database, setDatabase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [failedChecks, setFailedChecks] = useState<DbServerCheckResult | null>(null);
  const [result, setResult] = useState<DbServerCheckResult | null>(null);

  const running = prepare.isPending;
  const canSubmit = user.trim().length > 0 && password.length > 0 && !running;

  function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setFailedChecks(null);
    prepare.mutate(
      {
        id: server.id,
        input: {
          superuserUser: user.trim(),
          superuserPassword: password,
          ...(database.trim() ? { superuserDatabase: database.trim() } : {}),
        },
      },
      {
        onSuccess: (res) => {
          // Hasło administratora nie zostaje w pamięci ekranu ani w cache mutacji.
          setPassword('');
          prepare.reset();
          setResult(res.result);
          toast.show({ variant: 'success', title: t('admin.dbServers.toast.prepared') });
        },
        onError: (err) => {
          // Po błędzie też czyścimy hasło — do ponowienia trzeba wpisać je jeszcze raz.
          setPassword('');
          prepare.reset();
          const { code, message, data } = dbServerErrorInfo(err);
          const key = code ? ERROR_KEYS[code] : undefined;
          const base = key ? t(key) : t('admin.dbServers.prepare.errors.generic');
          setError(message && message !== base ? `${base} ${message}` : base);
          const res = data?.result as DbServerCheckResult | undefined;
          if (res && Array.isArray(res.checks)) setFailedChecks(res);
        },
      },
    );
  }

  function handleClose() {
    if (running) return;
    setPassword('');
    prepare.reset();
    onClose();
  }

  return (
    <Modal
      open
      onClose={handleClose}
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      title={t('admin.dbServers.prepare.title', { name: server.name })}
      size="md"
      footer={
        result ? (
          <Button variant="primary" onClick={handleClose} data-testid="prepare-close">
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={running}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              loading={running}
              disabled={!canSubmit}
              data-testid="prepare-submit"
            >
              {t('admin.dbServers.prepare.submit')}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-3" data-testid="prepare-result">
          <p className="text-sm text-text-primary">{t('admin.dbServers.prepare.success')}</p>
          <DbServerChecksList result={result} />
        </div>
      ) : (
        <form
          id={formId}
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          className="flex flex-col gap-4"
          data-testid="prepare-form"
        >
          <p className="text-sm text-text-secondary">{t('admin.dbServers.prepare.intro')}</p>
          <p className="text-xs text-text-tertiary">
            {server.host}:{server.port}
          </p>

          <FormField label={t('admin.dbServers.prepare.user')} required>
            <Input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              disabled={running}
              name="pg-superuser"
              autoComplete="off"
              data-testid="prepare-user"
            />
          </FormField>
          <FormField label={t('admin.dbServers.prepare.password')} required>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={running}
              name="pg-superuser-password"
              autoComplete="new-password"
              data-testid="prepare-password"
            />
          </FormField>
          <FormField
            label={t('admin.dbServers.prepare.database')}
            hint={t('admin.dbServers.prepare.databaseHint')}
          >
            <Input
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              placeholder="postgres"
              disabled={running}
              name="pg-superuser-database"
              autoComplete="off"
              data-testid="prepare-database"
            />
          </FormField>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
              data-testid="prepare-error"
            >
              {error}
            </div>
          )}
          {failedChecks && <DbServerChecksList result={failedChecks} />}
        </form>
      )}
    </Modal>
  );
}
